import React, { useCallback, useState } from "react";
import {
  Stack,
  Title,
  Text,
  Button,
  Group,
  Card,
  ActionIcon,
  Menu,
  Modal,
  TextInput,
  Alert,
  Anchor,
  Box,
  List,
  ThemeIcon,
  Table,
} from "@mantine/core";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconUsers,
  IconPercentage,
  IconClipboardList,
  IconTrash,
  IconGripVertical,
  IconStack2,
  IconAlertCircle,
  IconArrowsSort,
  IconCategory,
  IconCoin,
} from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import usePermissions from "../../hooks/usePermissions";
import { useSalonScopedFetch } from "../../hooks/useSalonScopedFetch";
import { filterActiveStaff } from "../../utils/staffUtils";
import { syncSubject } from "../../utils/staffTierSyncCopy";
import { Employee } from "../../types/salon";
import { StaffTier } from "../../types/staffTier";
import { StandardTableShell } from "../common/StandardTable/StandardTable";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import StaffTierMembersCell from "./StaffTierMembersCell";
import StaffTierAddRemoveModal from "./StaffTierAddRemoveModal";
import DeleteTierModal from "./DeleteTierModal";
import TierSyncModal from "./TierSyncModal";
import StaffTierCommissionDefaults from "./StaffTierCommissionDefaults";
import StaffTierServiceDefaults from "./StaffTierServiceDefaults";
import {
  SettingsPageLayout,
  SettingsPageHeader,
  SettingsListSkeleton,
} from "./common";

const byOrder = (a: StaffTier, b: StaffTier): number =>
  a.sort_order - b.sort_order || a.name.localeCompare(b.name);

// Lock dragging to the vertical axis (no @dnd-kit/modifiers dependency), so a
// row can't drift sideways out of the fixed reorder box.
const lockToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

interface SyncCtx {
  tier: StaffTier;
  staffIds: string[];
  name: string;
}

/** A draggable row shown only while reordering, inside a fixed-height box. */
const EditOrderRow: React.FC<{ tier: StaffTier; last: boolean }> = ({
  tier,
  last,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tier.id });
  return (
    <Group
      ref={setNodeRef}
      gap="sm"
      wrap="nowrap"
      px="md"
      py="sm"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        background: isDragging
          ? "var(--mantine-color-gray-0)"
          : "var(--mantine-color-body)",
        borderBottom: last
          ? undefined
          : "1px solid var(--mantine-color-gray-2)",
      }}
    >
      <ActionIcon
        variant="subtle"
        color="gray"
        aria-label={`Drag ${tier.name}`}
        style={{ cursor: "grab", touchAction: "none" }}
        {...attributes}
        {...listeners}
      >
        <IconGripVertical size={18} />
      </ActionIcon>
      <Text fw={600}>{tier.name}</Text>
    </Group>
  );
};

const StaffTiersSettings: React.FC = () => {
  const { businessInfo, employees, setEmployees, refreshServices } =
    useSalon() as {
      businessInfo: { id?: string };
      employees: Employee[] | null;
      setEmployees: (
        updater: (prev: Employee[] | null) => Employee[] | null,
      ) => void;
      refreshServices: () => Promise<unknown>;
    };
  const salonId = businessInfo?.id;
  const {
    getStaffTiers,
    createStaffTier,
    updateStaffTier,
    reorderStaffTiers,
    deleteStaffTier,
    assignStaffTier,
    applyTierCommissionDefaults,
    applyTierServiceDefaults,
  } = useApp();

  const perms = usePermissions();
  const tierPerms = perms?.staffTiers;
  const canManage = tierPerms?.canCreateTiers !== false;
  const canDelete = tierPerms?.canDeleteTiers !== false;
  const canAssign = tierPerms?.canAssignStaffToTiers !== false;

  const [tiers, setTiers] = useState<StaffTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Reorder (Edit Order) mode.
  const [orderDraft, setOrderDraft] = useState<StaffTier[] | null>(null);

  // Create / rename modal.
  const [formOpen, setFormOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<StaffTier | null>(null);
  const [nameInput, setNameInput] = useState("");

  // Per-tier modals.
  const [addRemoveTier, setAddRemoveTier] = useState<StaffTier | null>(null);
  const [commissionTier, setCommissionTier] = useState<StaffTier | null>(null);
  const [serviceTier, setServiceTier] = useState<StaffTier | null>(null);
  const [deleteTier, setDeleteTier] = useState<StaffTier | null>(null);
  const [emptyDeleteTier, setEmptyDeleteTier] = useState<StaffTier | null>(
    null,
  );

  // Post-assign sync prompt.
  const [syncCtx, setSyncCtx] = useState<SyncCtx | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const load = useCallback(async (): Promise<void> => {
    if (!salonId) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await getStaffTiers(salonId);
      setTiers([...(res.staff_tiers || [])].sort(byOrder));
    } catch (error) {
      console.error("Error loading staff tiers:", error);
      setLoadError("Couldn't load staff tiers. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [salonId, getStaffTiers]);

  const { retry } = useSalonScopedFetch(salonId, load, { tag: "staff-tiers" });

  const membersOf = useCallback(
    (tier: StaffTier): Employee[] =>
      (employees || []).filter((e) => e.tier_id === tier.id),
    [employees],
  );
  const assignableFor = useCallback(
    (tier: StaffTier): Employee[] =>
      (filterActiveStaff(employees || []) as Employee[]).filter(
        (e) => e.tier_id !== tier.id,
      ),
    [employees],
  );

  const patchEmployee = (staffId: string, tier: StaffTier | null): void =>
    setEmployees((prev) =>
      (prev || []).map((e) =>
        e.id === staffId
          ? { ...e, tier_id: tier?.id ?? null, tier_name: tier?.name ?? null }
          : e,
      ),
    );

  // ---- create / rename ----
  const openCreate = (): void => {
    setEditingTier(null);
    setNameInput("");
    setActionError(null);
    setFormOpen(true);
  };
  const openRename = (tier: StaffTier): void => {
    setEditingTier(tier);
    setNameInput(tier.name);
    setActionError(null);
    setFormOpen(true);
  };
  const submitName = async (): Promise<void> => {
    const name = nameInput.trim();
    if (!name || !salonId) return;
    setSaving(true);
    setActionError(null);
    try {
      if (editingTier) {
        await updateStaffTier(editingTier.id, { name });
      } else {
        await createStaffTier(salonId, { name });
      }
      setFormOpen(false);
      await load();
    } catch (error) {
      console.error("Error saving staff tier:", error);
      setActionError(
        editingTier
          ? "Couldn't rename this tier. The name may already be in use."
          : "Couldn't create this tier. The name may already be in use.",
      );
    } finally {
      setSaving(false);
    }
  };

  // ---- reorder ----
  const startReorder = (): void => setOrderDraft([...tiers]);
  const cancelReorder = (): void => setOrderDraft(null);
  const onDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id || !orderDraft) return;
    const from = orderDraft.findIndex((t) => t.id === active.id);
    const to = orderDraft.findIndex((t) => t.id === over.id);
    setOrderDraft(arrayMove(orderDraft, from, to));
  };
  const saveReorder = async (): Promise<void> => {
    if (!salonId || !orderDraft) return;
    setSaving(true);
    setActionError(null);
    try {
      const res = await reorderStaffTiers(salonId, {
        order: orderDraft.map((t, i) => ({ id: t.id, sort_order: i })),
      });
      setTiers([...(res.staff_tiers || [])].sort(byOrder));
      setOrderDraft(null);
    } catch (error) {
      console.error("Error reordering staff tiers:", error);
      setActionError("Couldn't save the new order. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- assign / move / sync ----
  const offersSync = (tier: StaffTier): boolean =>
    tier.has_service_defaults || tier.has_commission_defaults;

  // Perform the actual tier assignment (or unparent) for one or many staff.
  const doAssign = async (
    staffIds: string[],
    target: StaffTier | null,
  ): Promise<void> => {
    if (!salonId) return;
    for (const id of staffIds) {
      await assignStaffTier(salonId, id, { tier_id: target?.id ?? null });
      patchEmployee(id, target);
    }
    await load();
  };

  // Single member move (members-column move icon). Unparenting or a tier with
  // no defaults assigns directly; a tier WITH defaults surfaces the Sync
  // confirmation BEFORE the assignment is made.
  const moveMember = async (
    staffId: string,
    target: StaffTier | null,
    label: string,
  ): Promise<void> => {
    setActionError(null);
    if (!target || !offersSync(target)) {
      try {
        await doAssign([staffId], target);
      } catch (error) {
        console.error("Error moving staff member:", error);
        setActionError("Couldn't update this staff member's tier.");
      }
      return;
    }
    setSyncCtx({ tier: target, staffIds: [staffId], name: label });
  };

  // Add staff from the Add/Remove modal. Same confirm-before-assign rule.
  const addStaff = async (
    tier: StaffTier,
    staffIds: string[],
  ): Promise<void> => {
    setActionError(null);
    setAddRemoveTier(null);
    if (!offersSync(tier)) {
      setAssignBusy(true);
      try {
        await doAssign(staffIds, tier);
      } catch (error) {
        console.error("Error adding staff to tier:", error);
        setActionError("Couldn't add staff to this tier.");
      } finally {
        setAssignBusy(false);
      }
      return;
    }
    setSyncCtx({ tier, staffIds, name: syncSubject(staffIds.length) });
  };

  const removeStaff = async (staffId: string): Promise<void> => {
    if (!salonId) return;
    setAssignBusy(true);
    setActionError(null);
    try {
      await assignStaffTier(salonId, staffId, { tier_id: null });
      patchEmployee(staffId, null);
      await load();
    } catch (error) {
      console.error("Error removing staff from tier:", error);
      setActionError("Couldn't remove this staff member.");
    } finally {
      setAssignBusy(false);
    }
  };

  // Apply the selected tier defaults to already-assigned staff. Commission and
  // service applies are independent; a failure of one is swallowed so the other
  // still lands.
  const applyDefaults = async (
    tier: StaffTier,
    staffIds: string[],
    applyService: boolean,
    applyCommission: boolean,
  ): Promise<void> => {
    if (applyCommission) {
      try {
        await applyTierCommissionDefaults(tier.id, {
          staff_ids: staffIds,
          acknowledge: true,
        });
      } catch (e) {
        console.error("Commission sync failed:", e);
      }
    }
    if (applyService) {
      try {
        await applyTierServiceDefaults(tier.id, {
          staff_ids: staffIds,
          acknowledge: true,
        });
        // Applying replaces each member's service menu on the backend; refetch
        // so the in-memory services (with nested per-staff config) aren't stale.
        await refreshServices();
      } catch (e) {
        console.error("Service sync failed:", e);
      }
    }
  };

  // Confirm: assign the pending staff first, THEN apply the chosen defaults.
  const runSync = async (
    applyService: boolean,
    applyCommission: boolean,
  ): Promise<void> => {
    if (!syncCtx) return;
    const { tier, staffIds } = syncCtx;
    setSyncing(true);
    try {
      await doAssign(staffIds, tier);
      await applyDefaults(tier, staffIds, applyService, applyCommission);
    } catch (error) {
      console.error("Error applying tier defaults:", error);
      setActionError("Couldn't update the staff member's tier.");
    } finally {
      setSyncing(false);
      setSyncCtx(null);
    }
  };

  // Skip: complete the assignment without applying any defaults.
  const skipSync = async (): Promise<void> => {
    if (!syncCtx) return;
    const { tier, staffIds } = syncCtx;
    setSyncing(true);
    try {
      await doAssign(staffIds, tier);
    } catch (error) {
      console.error("Error assigning staff to tier:", error);
      setActionError("Couldn't update the staff member's tier.");
    } finally {
      setSyncing(false);
      setSyncCtx(null);
    }
  };

  // ---- delete ----
  const onDeleteClick = (tier: StaffTier): void => {
    if (tier.assigned_staff_count > 0) {
      setDeleteTier(tier);
    } else {
      setEmptyDeleteTier(tier);
    }
  };
  const confirmEmptyDelete = async (): Promise<void> => {
    if (!emptyDeleteTier) return;
    setSaving(true);
    try {
      await deleteStaffTier(emptyDeleteTier.id);
      setEmptyDeleteTier(null);
      await load();
    } catch (error) {
      console.error("Error deleting staff tier:", error);
      setActionError("Couldn't delete this tier. Please try again.");
      setEmptyDeleteTier(null);
    } finally {
      setSaving(false);
    }
  };

  const deleteBlocked = (tier: StaffTier): boolean =>
    tier.assigned_staff_count > 0 && !(canDelete && canAssign);

  // ---- render ----
  const renderEmptyState = (): React.ReactNode => (
    <Card withBorder radius="md" p="xl">
      <Stack align="center" gap="md" py="lg">
        <ThemeIcon
          size={64}
          radius="xl"
          variant="light"
          color="var(--brand-teal)"
        >
          <IconStack2 size={34} />
        </ThemeIcon>
        <Title order={3} ta="center">
          Organize your team with staff tiers
        </Title>
        <Text c="dimmed" ta="center" maw={520}>
          Group staff by their experience or seniority to streamline changes
          across your business.
        </Text>
        <List spacing="xs" size="sm" center maw={520}>
          <List.Item
            icon={
              <ThemeIcon color="var(--brand-teal)" size={22} radius="xl">
                <IconCategory size={14} />
              </ThemeIcon>
            }
          >
            Categorize workers so it&apos;s easy to see who&apos;s at each level
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="var(--brand-teal)" size={22} radius="xl">
                <IconClipboardList size={14} />
              </ThemeIcon>
            }
          >
            Set default services, durations, and prices per tier
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="var(--brand-teal)" size={22} radius="xl">
                <IconCoin size={14} />
              </ThemeIcon>
            }
          >
            Define a default commission structure per tier
          </List.Item>
        </List>
      </Stack>
    </Card>
  );

  const renderKebab = (tier: StaffTier): React.ReactNode => {
    const blocked = deleteBlocked(tier);
    return (
      <Menu position="bottom-end" withinPortal shadow="md">
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={`Actions for ${tier.name}`}
          >
            <IconDotsVertical size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {canManage && (
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={() => openRename(tier)}
            >
              Edit Tier Name
            </Menu.Item>
          )}
          {canAssign && (
            <Menu.Item
              leftSection={<IconUsers size={14} />}
              onClick={() => setAddRemoveTier(tier)}
            >
              Add / Remove Staff
            </Menu.Item>
          )}
          {(canManage || canAssign) && (
            <Menu.Item
              leftSection={<IconPercentage size={14} />}
              onClick={() => setCommissionTier(tier)}
            >
              Edit Default Commission Structure
            </Menu.Item>
          )}
          {(canManage || canAssign) && (
            <Menu.Item
              leftSection={<IconClipboardList size={14} />}
              onClick={() => setServiceTier(tier)}
            >
              Edit Default Service List
            </Menu.Item>
          )}
          {canDelete && (
            <>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                disabled={blocked}
                onClick={() => onDeleteClick(tier)}
              >
                Delete Tier
              </Menu.Item>
              {blocked && (
                <Text size="xs" c="dimmed" px="sm" pt={2} pb="xs" maw={240}>
                  Deleting a tier with staff also reassigns them — you need
                  permission to assign staff to tiers, or move the staff out
                  first.
                </Text>
              )}
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  };

  const reordering = orderDraft !== null;

  // Header actions: Cancel / Save while reordering; otherwise the primary
  // Add Tier (the only create entry point, including on the empty state),
  // plus Edit Order once there are tiers to reorder.
  const renderHeaderActions = (): React.ReactNode => {
    if (reordering) {
      return (
        <Group gap="xs" wrap="nowrap">
          <Button variant="default" onClick={cancelReorder} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={saveReorder}
            loading={saving}
          >
            Save order
          </Button>
        </Group>
      );
    }
    if (!canManage) return undefined;
    return (
      <Group gap="xs" wrap="nowrap">
        {tiers.length > 0 && (
          <Button
            variant="default"
            leftSection={<IconArrowsSort size={16} />}
            onClick={startReorder}
            disabled={tiers.length < 2}
          >
            Edit Order
          </Button>
        )}
        <Button
          leftSection={<IconPlus size={16} />}
          color="var(--brand-teal)"
          onClick={openCreate}
        >
          Add Tier
        </Button>
      </Group>
    );
  };

  return (
    <SettingsPageLayout
      header={
        <SettingsPageHeader
          title="Staff Tiers"
          subtitle="Group staff into tiers with shared defaults."
          actions={renderHeaderActions()}
        />
      }
    >
      {loadError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          withCloseButton
          onClose={() => setLoadError(null)}
        >
          {loadError}{" "}
          <Button variant="subtle" size="compact-sm" onClick={retry}>
            Retry
          </Button>
        </Alert>
      )}
      {actionError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          withCloseButton
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      {/* First load only: later reloads keep the table and show its overlay. */}
      {isLoading && tiers.length === 0 && (
        <SettingsListSkeleton
          ariaLabel="Loading staff tiers"
          data-testid="staff-tiers-loading"
        />
      )}

      {!isLoading && !loadError && tiers.length === 0 && renderEmptyState()}

      {/* Reorder mode: a fixed-height, self-scrolling box so rows can't drag
          past its bounds or make the page grow. */}
      {tiers.length > 0 && reordering && (
        <Box
          style={{
            maxHeight: 360,
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            modifiers={[lockToVerticalAxis]}
          >
            <SortableContext
              items={(orderDraft as StaffTier[]).map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              {(orderDraft as StaffTier[]).map((tier, i) => (
                <EditOrderRow
                  key={tier.id}
                  tier={tier}
                  last={i === (orderDraft as StaffTier[]).length - 1}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {tiers.length > 0 && !reordering && (
        <StandardTableShell
          loading={isLoading || saving}
          minWidth={860}
          ariaLabel="Staff tiers"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tier</Table.Th>
              <Table.Th>Members</Table.Th>
              <Table.Th>Service Defaults</Table.Th>
              <Table.Th>Commission Defaults</Table.Th>
              <Table.Th w={60} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tiers.map((tier) => (
              <Table.Tr key={tier.id}>
                <Table.Td>
                  <Text fw={600}>{tier.name}</Text>
                </Table.Td>
                <Table.Td>
                  <StaffTierMembersCell
                    tier={tier}
                    tiers={tiers}
                    members={membersOf(tier)}
                    canAssign={canAssign}
                    onMove={moveMember}
                  />
                </Table.Td>
                <Table.Td>
                  {tier.has_service_defaults ? (
                    <Anchor
                      component="button"
                      type="button"
                      onClick={() => setServiceTier(tier)}
                    >
                      View here
                    </Anchor>
                  ) : (
                    <Text c="dimmed">N/A</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {tier.has_commission_defaults ? (
                    <Anchor
                      component="button"
                      type="button"
                      onClick={() => setCommissionTier(tier)}
                    >
                      View here
                    </Anchor>
                  ) : (
                    <Text c="dimmed">N/A</Text>
                  )}
                </Table.Td>
                <Table.Td>{renderKebab(tier)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </StandardTableShell>
      )}

      {/* Create / rename */}
      <Modal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingTier ? "Edit tier name" : "Create tier"}
        centered
      >
        <Stack>
          <TextInput
            label="Tier name"
            placeholder="e.g. Senior Stylist"
            value={nameInput}
            onChange={(e) => setNameInput(e.currentTarget.value)}
            data-autofocus
            maxLength={128}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitName();
            }}
          />
          {actionError && (
            <Text c="red" size="sm">
              {actionError}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              loading={saving}
              disabled={!nameInput.trim()}
              onClick={submitName}
            >
              {editingTier ? "Save" : "Create"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add / remove staff */}
      {addRemoveTier && (
        <StaffTierAddRemoveModal
          opened={!!addRemoveTier}
          onClose={() => setAddRemoveTier(null)}
          tier={addRemoveTier}
          members={membersOf(addRemoveTier)}
          assignable={assignableFor(addRemoveTier)}
          busy={assignBusy}
          onAdd={(ids) => addStaff(addRemoveTier, ids)}
          onRemove={removeStaff}
        />
      )}

      {/* Commission defaults */}
      <Modal
        opened={!!commissionTier}
        onClose={() => {
          setCommissionTier(null);
          load();
        }}
        title={`Commission structure — ${commissionTier?.name ?? ""}`}
        centered
        size="lg"
      >
        {commissionTier && (
          <StaffTierCommissionDefaults
            tier={commissionTier}
            canManage={canManage}
            canAssign={canAssign}
            onClose={() => {
              setCommissionTier(null);
              load();
            }}
          />
        )}
      </Modal>

      {/* Service defaults */}
      <Modal
        opened={!!serviceTier}
        onClose={() => {
          setServiceTier(null);
          load();
        }}
        title={`Service defaults — ${serviceTier?.name ?? ""}`}
        centered
        size="xxl"
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          },
        }}
      >
        {serviceTier && (
          <StaffTierServiceDefaults
            tier={serviceTier}
            canManage={canManage}
            canAssign={canAssign}
          />
        )}
      </Modal>

      {/* Delete populated tier (reassign) */}
      {deleteTier && (
        <DeleteTierModal
          opened={!!deleteTier}
          onClose={() => setDeleteTier(null)}
          tier={deleteTier}
          tiers={tiers}
          members={membersOf(deleteTier)}
          onDeleted={async () => {
            setDeleteTier(null);
            await load();
          }}
        />
      )}

      {/* Delete empty tier (simple) */}
      <DeleteConfirmationModal
        isOpen={!!emptyDeleteTier}
        onClose={() => setEmptyDeleteTier(null)}
        onConfirm={confirmEmptyDelete}
        title="Delete staff tier"
        message={`Delete the "${emptyDeleteTier?.name}" tier? This can't be undone.`}
        isLoading={saving}
        confirmLabel="Delete tier"
      />

      {/* Pre-assignment sync confirmation (surfaced before the tier label is
          set — Confirm assigns then applies; Skip assigns only; Cancel aborts). */}
      {syncCtx && (
        <TierSyncModal
          opened={!!syncCtx}
          onClose={() => setSyncCtx(null)}
          tierName={syncCtx.tier.name}
          offerServiceReplace={syncCtx.tier.has_service_defaults}
          offerCommission={syncCtx.tier.has_commission_defaults}
          targetName={syncCtx.name}
          loading={syncing}
          confirmLabel="Assign & apply"
          skipLabel="Assign without applying"
          onSkip={skipSync}
          onConfirm={({ applyService, applyCommission }) =>
            runSync(applyService, applyCommission)
          }
        />
      )}
    </SettingsPageLayout>
  );
};

export default StaffTiersSettings;
